import { DehydratedState, useQuery } from "react-query"
import { memo, useEffect } from "react"
import dynamic from "next/dynamic"
import { useRouter } from "next/router"
import { fetchProductsList } from "@/api/productsAPI"
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs"
import { BREADCRUMB_CATALOG_DEFAULT } from "@/components/Breadcrumbs/constants"
import { CompanionsContainer } from "@/components/Products/Detail/Containers/CompanionsContainer"
import { ProductJsonLd } from "@/components/Products/ProductPage/ProductJsonLd"
import { ProductQaJsonLd } from "@/components/Products/ProductPage/ProductQAJsonLd"
import { LazyHydrateContainer } from "@/hoc/LazyHydrate"
import {
  getMetaDescriptionProduct,
  getMetaTitleProduct,
} from "@/hooks/product/helpers"
import { useAppSelector } from "@/hooks/redux"
import { Col, Container, Row } from "@/styles/utils/StyledGrid"
import { Button } from "@/ui/Button/Button"
import { Typography } from "@/ui/Typography/Typography"
import { trackGTM } from "@/utils/analytics"
import { setLastCategoryStorage } from "@/utils/categories/helpers"
import {
  convertPennyToRub,
  getImagePath,
  getMilisecondsHours,
  sortProductsWeightRule,
} from "@/utils/common/helpers"
import { ROUTES, SITE_URL, TITLE_SITE_RU } from "@/utils/constants"
import { Meta } from "../../Meta/Meta"
import { Detail } from "../Detail/Detail"
import { StyledDetailProduct } from "../Detail/StyledDetail"
import { Side, StyledProductPage } from "../StyledProductPage"
import type { ProductType } from "../types"

type SSRPropsReturnType = {
  product: ProductType | null
  alias: string
  dehydratedState: DehydratedState
}

const { trackProductDetail } = trackGTM

const DetailProductPageRecommends = dynamic(
  () =>
    import("@/components/LeadHit").then(
      ({ DetailProductPageRecommends }) => DetailProductPageRecommends,
    ),
  {
    ssr: false,
  },
)

const ProductPage = memo(({ product: initialProduct }: SSRPropsReturnType) => {
  const {
    uuid,
    name = "",
    images = [],
    total_qty = 0,
    price = 0,
    alias = "",
    companions = [],
    variations = [],
    unit = "",
    parent,
    containers: { only_containers = false, units = [] } = {},
  } = initialProduct || {}
  const router = useRouter()
  const breadcrumbs = useAppSelector((state) => state.catalog.breadcrumbs)

  const metaTitle = getMetaTitleProduct({ name })
  const metaDescription = getMetaDescriptionProduct({
    name,
    price,
    unitMeasure: unit,
    totalQty: total_qty,
    units,
    parentKit: parent,
    isOnlyContainers: Boolean(only_containers),
  })
  const metaImage = images.length > 0 ? getImagePath(images[0]) : ""

  const uuidsCompanions = companions.map((c) => c.uuid).join(",")

  const { data: companionsData } = useQuery(
    ["companions", uuidsCompanions],
    () =>
      fetchProductsList({
        uuids: uuidsCompanions,
        server: true,
      }).catch((err) => console.log("err [companions] ", err)),
    {
      enabled: uuidsCompanions.length > 0,
      staleTime: getMilisecondsHours(1),
    },
  )

  const companionsProductsSorted = sortProductsWeightRule({
    products: companionsData || [],
    rules: companions,
  })

  useEffect(() => {
    const onRouteChangeStart = () => {
      setLastCategoryStorage(null)
    }
    router.events.on("routeChangeStart", onRouteChangeStart)

    return () => {
      router.events.off("routeChangeStart", onRouteChangeStart)
    }
  }, [router.events])

  useEffect(() => {
    const selectedVariation = variations.find((v) => v.alias === alias)
    const variationString = !!selectedVariation
      ? [selectedVariation.type, selectedVariation.name].join(": ")
      : ""
    trackProductDetail({
      product: {
        id: uuid || "",
        name,
        brand: TITLE_SITE_RU,
        price: convertPennyToRub(price),
        quantity: total_qty,
        variant: variationString,
      },
    })
  }, [alias, name, price, total_qty, uuid, variations])

  return (
    <>
      <Meta
        title={metaTitle}
        description={metaDescription}
        ogTags={{
          title: metaTitle,
          url: `${SITE_URL}${router.asPath}`,
          image: metaImage,
        }}
      >
        <>
          <ProductJsonLd
            name={metaTitle}
            description={metaDescription}
            image={metaImage}
            totalQty={total_qty}
            price={price}
            alias={alias}
          />
          <ProductQaJsonLd name={name} />
        </>
      </Meta>
      <StyledProductPage>
        <Container>
          <Row>
            <Breadcrumbs
              breadcrumbs={{ ...BREADCRUMB_CATALOG_DEFAULT, ...breadcrumbs }}
            />

            <StyledDetailProduct>
              <Side>
                <CompanionsContainer
                  products={companionsProductsSorted}
                  ruleSort={companions}
                />
                <LazyHydrateContainer>
                  <DetailProductPageRecommends />
                </LazyHydrateContainer>
              </Side>

              {!!initialProduct ? (
                <Detail
                  product={initialProduct}
                  companions={companionsProductsSorted}
                />
              ) : (
                <Row>
                  <Col>
                    <Typography variant={"h1"}>Произошла ошибка</Typography>
                    <Button
                      href={ROUTES.catalog}
                      as={"a"}
                      variant={"filled"}
                      size={"large"}
                    >
                      В каталог
                    </Button>
                  </Col>
                </Row>
              )}
            </StyledDetailProduct>
          </Row>
        </Container>
      </StyledProductPage>
    </>
  )
})

ProductPage.displayName = "ProductPage"

export type { SSRPropsReturnType }
export { ProductPage }
